/**
 * Where the game runs, and that platform's own entry chunk, loaded before anything else
 * (main.ts imports this module first):
 *
 *   Capacitor (iOS/Android)  src/native: plugin loaders, the launch link (or a first launch's
 *                            install referrer), AdMob units, sign-in sheets, the native shell
 *   Discord Activity         src/discord: the Embedded App SDK, mapped service URLs, Discord sign-in
 *
 * A web visitor downloads neither. The chunk is awaited at the top level because the services
 * need what it returns before they exist. Rollup treats every dynamic import of a module that
 * uses top-level await as awaited, and never lets an awaited chunk import from the entry chunk
 * (the entry would still be evaluating: a deadlock); anything they share gets a chunk of its
 * own, which every web visitor then loads up front. Hence this separate module (main.ts's other
 * dynamic imports stay ordinary), chunks that import the game's types only, and plugin imports
 * in src/native/loaders.ts that bypass Vite's preload helper (test/native/boundary.test.ts).
 */
import { detectPlatform } from '@mp/adapters/platform';
import { browserKV } from './services/storage.js';
import { PlayerStore } from './services/store.js';

export const params = new URLSearchParams(location.search);
/**
 * `?e2e=1` installs the test hooks (window.__mp, which includes the solver's next tap), short ads and
 * fixed clocks. Only dev builds and builds made for the e2e suites (VITE_E2E=1) honour it: in a public
 * build anyone could ask for the best move in a duel, a cup or a Rush round.
 */
export const e2e = (import.meta.env.DEV || import.meta.env.VITE_E2E === '1') && params.get('e2e') === '1';
export const kv = browserKV(e2e ? 'mp-e2e:' : 'mp:');

/** A failure before main.ts's error boundary exists: uncover the HTML splash's Retry (index.html reacts only to script load errors). */
export function bootFailed(e: unknown): never {
  window.__mpBootFailed?.();
  throw e;
}

export const platform = detectPlatform();
export const native = platform.native
  ? await import('./native/index.js').then((m) => m.bootNative(platform, { firstLaunch: !PlayerStore.exists(kv) })).catch(bootFailed)
  : null;
export const discord = platform.kind === 'discord' ? await import('./discord/index.js').then((m) => m.bootDiscord(platform)).catch(bootFailed) : null;
